/**
 * 261030 S10 (M5/H5) — FE của «không có ngõ cụt im lặng». CHỈ logic thuần (không React / antd / router; không import utils Four khác ⇒ không vòng import).
 *  · `status.terminal` (BE `terminalViewOf`): câu Việt + việc làm được cho trạng thái chuỗi — dùng làm câu DỰ PHÒNG khi FE chưa có bảng riêng cho mã (không bao giờ in mã thô UNCERTAIN/UNKNOWN…).
 *  · `nextActionPrimaryOf`: MỘT switch vét cạn (`assertNever`) theo `nextAction.kind` — thêm kind mới ở BE mà quên ở đây thì tsc vỡ + snapshot test vỡ.
 *  · `plainWhyOf`: lý do của nút tắt, không tiền, không «(MÃ_THÔ)».
 *  · `ownerStalledOf`: chuỗi RUNNING lâu bất thường ⇒ hiện lối ra (BE đã tự đóng chuỗi mồ côi sau ~1 phút; đây là lưới thứ hai cho việc chạy thật lâu).
 */
import { assertNever } from './workflowFourSilent';
import { stripMoneyOf } from './moneyText';

export type FourNextActionKind = 'REFRESH_STAGING' | 'MERGE_PROPS' | 'SAFETY_BLOCKED';
export interface FourTerminalAction { kind: 'START' | 'STOP' | 'RESUME' | 'RELOAD' | 'ABANDON_PLAN' | 'ABANDON_UNKNOWN' | 'NEXT_STEP'; label: string; enabled: boolean; why: string | null }
export interface FourTerminalView { code: string; messageVi: string; actions: FourTerminalAction[]; waitingFor?: string }
export interface FourNextAction { label: string; step: string; enabled: boolean; why: string | null; kind?: FourNextActionKind; /** S12c fix3: cổng vừa duyệt, máy đang khởi động bước kế (BE giới hạn 60 s) — nút tắt có lý do, KHÔNG «Sang bước N». */ opening?: boolean }

const VI = /[àáảãạăắằẳẵặâấầẩẫậèéẻẽẹêếềểễệìíỉĩịòóỏõọôốồổỗộơớờởỡợùúủũụưứừửữựỳýỷỹỵđ]/i;
export const looksVietnamese = (s: string | null | undefined): boolean => !!s && VI.test(s);
/** Mã thô đứng một mình trong ngoặc: «(BLOCKED_INPUT)», «(HTTP_400)». */
const CODE_PAREN_RE = /\s*\((?:[A-Z][A-Z0-9]*_[A-Z0-9_]+|[A-Z]{4,}|P\d{4})\)/g;
/** Lý do hiển thị của một nút tắt: không tiền, không mã thô trong ngoặc. Rỗng ⇒ null. */
export function plainWhyOf(why: string | null | undefined): string | null {
  if (!why) return null;
  const out = (stripMoneyOf(why) ?? '').replace(CODE_PAREN_RE, '').replace(/\s{2,}/g, ' ').trim();
  return out || null;
}
/** Câu Việt do BE dịch cho trạng thái chuỗi hiện tại (null khi BE cũ chưa có `terminal`). */
export const terminalMessageOf = (status: { terminal?: FourTerminalView | null } | null | undefined): string | null => {
  const m = status?.terminal?.messageVi;
  return typeof m === 'string' && m.trim() ? m.trim() : null;
};
/** Câu cho banner lỗi: `failDetail.message` nếu đã là tiếng Việt, ngược lại câu BE dịch; không có gì ⇒ câu chung. KHÔNG bao giờ in `failReason` thô. */
export function failTextOf(chain: { failDetail?: string | { message?: string } | null } | null | undefined, terminalMessage: string | null, fallback: string): string {
  const raw = typeof chain?.failDetail === 'string' ? chain.failDetail : chain?.failDetail?.message;
  if (raw && looksVietnamese(raw)) return plainWhyOf(raw) ?? fallback;
  return terminalMessage ?? fallback;
}

export interface NextActionPrimary { label: string; enabled: boolean; why: string | null; mergeProps?: boolean; refreshStaging?: boolean }
/** Nút chính theo `nextAction.kind` — vét cạn. `kind` vắng ⇒ nút thường theo `enabled`. */
export function nextActionPrimaryOf(na: FourNextAction): NextActionPrimary {
  const base = { label: stripMoneyOf(na.label), enabled: na.enabled, why: plainWhyOf(na.why) };
  const kind: FourNextActionKind | undefined = na.kind;
  switch (kind) {
    case undefined: return base;
    case 'MERGE_PROPS': return { ...base, mergeProps: true };
    case 'REFRESH_STAGING': return { ...base, refreshStaging: true };
    case 'SAFETY_BLOCKED': return { ...base, enabled: false, why: base.why ?? 'Bị bộ lọc nội dung — sửa chữ khung hoặc «Bỏ qua tấm này» rồi chạy tiếp.' };
    default: return assertNever(kind, 'nextAction.kind');
  }
}

const STEP_NO: Record<string, number> = { STEP1: 1, ASSETS: 2, BOARD: 3, VIDEO: 4 };
export type FourStepKeyLite = 'ASSETS' | 'BOARD' | 'VIDEO';
/** F1: `nextAction` thuộc BƯỚC KHÁC (vd. «Gen N video» của ④ trong khi đang xem bước ①) ⇒ nút «Sang bước N» thay vì mở hộp duyệt cổng 1. null ⇒ nextAction thuộc bước ①. */
export function otherStepPrimaryOf(na: FourNextAction): { label: string; enabled: true; why: string; goStep: FourStepKeyLite } | null {
  if (na.step === 'STEP1' || !(na.step in STEP_NO) || na.opening === true) return null;   // S12c fix3: đang khởi động ⇒ giữ nút tắt có lý do, không mời «Sang bước N» giữa chừng
  const n = STEP_NO[na.step];
  const what = stripMoneyOf(na.label);
  return { label: `Sang bước ${n}`, enabled: true, why: `Bước ① đã xong — việc tiếp theo nằm ở bước ${n}${what ? `: «${what}»` : ''}.`, goStep: na.step as FourStepKeyLite };
}

/** Chuỗi RUNNING mà `chain.updatedAt` quá cũ (phút) ⇒ nghi treo. null khi không áp dụng. Ngưỡng 30 phút: đoạn dài nhất đo được ≈ 8–20 phút. */
export const OWNER_STALL_MINUTES = 30;
export function ownerStalledOf(chain: { state?: string; updatedAt?: string | null } | null | undefined, nowMs: number): { minutes: number } | null {
  if (chain?.state !== 'RUNNING' || !chain.updatedAt) return null;
  const at = Date.parse(chain.updatedAt);
  if (!Number.isFinite(at)) return null;
  const minutes = Math.floor((nowMs - at) / 60_000);
  return minutes >= OWNER_STALL_MINUTES ? { minutes } : null;
}
